import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { ApiAuth, ApiUsers } from 'api';
import { IAuthUserMe } from 'api/apiUsers/models';
import { JWT_CACHE_KEY, USER_CACHE_KEY } from 'shared/consts';
import { LocalStorage } from 'tools';

interface IAuthSliceState {
  jwt: null | string;
  authLoading: boolean;
  authError: null | string;
  user: IAuthUserMe | null;
  isAdmin: boolean;
  isUser: boolean;
}

const lsJwt = LocalStorage.get<string>(JWT_CACHE_KEY);
const lsUser = LocalStorage.get<IAuthUserMe>(USER_CACHE_KEY);

const authSliceInitialState: IAuthSliceState = {
  jwt: lsJwt ?? null,
  authLoading: false,
  authError: null,
  user: lsUser ?? null,
  isAdmin: lsUser ? lsUser.role === 'admin' : false,
  isUser: lsUser ? lsUser.role !== 'admin' : false,
};

const onLogin = createAsyncThunk<
  any,
  { login: string; password: string; onSuccess: () => void },
  any
>('auth/login', async ({ login, password, onSuccess }, thunkApi) => {
  try {
    const { data } = await ApiAuth.login({ login, password });

    const jwt = data?.jwt ?? '';

    const { isSuccess, errorMessage, data: user } = await ApiUsers.getMe(jwt);

    if (isSuccess) {
      setTimeout(() => {
        onSuccess();
      }, 50);

      return thunkApi.fulfillWithValue({ jwt, user });
    } else {
      return thunkApi.rejectWithValue(errorMessage as string);
    }
  } catch (error) {
    console.log(error);
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState: authSliceInitialState as IAuthSliceState,
  extraReducers: builder => {
    // login
    builder.addCase(onLogin.pending, state => {
      state.authLoading = true;
    });
    builder.addCase(onLogin.rejected, (state, action) => {
      state.authError = action.payload as string;
      state.authLoading = false;
    });
    builder.addCase(
      onLogin.fulfilled,
      (state, { payload }: { payload: { jwt: string; user: IAuthUserMe } }) => {
        const { jwt, user } = payload;

        state.jwt = jwt;
        state.authLoading = false;
        state.authError = null;
        state.user = user;
        state.isAdmin = user.role === 'admin';
        state.isUser = user.role === 'user';

        LocalStorage.set(JWT_CACHE_KEY, jwt);
        LocalStorage.set(USER_CACHE_KEY, user);
      },
    );
  },
  reducers: {
    setAuthError: (state, { payload }: { payload: string | null }) => {
      state.authError = payload;
    },
    logout: state => {
      LocalStorage.remove(JWT_CACHE_KEY);
      LocalStorage.remove(USER_CACHE_KEY);

      state.jwt = null;
      state.authError = null;
      state.authLoading = false;
      state.isAdmin = false;
      state.isUser = false;
      state.user = null;
    },
  },
});

export { onLogin };
export const { setAuthError, logout } = authSlice.actions;

export default authSlice.reducer;
